import { Link, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { ChevronDown, Mail, Phone, MessageCircle, Menu, X } from "lucide-react";
import { BOOKING_URL } from "@/components/site/Layout";
import { services } from "@/data/services";
import { practiceAreas } from "@/data/practice-areas";
import { practiceIcons } from "@/components/icons/practiceIcons";
import { resourceIcons } from "@/components/icons/resourceIcons";
import { LocationsMenu, MobileLocationsMenu } from "./LocationsMenu";
import { UkFlag } from "@/components/icons/UkFlag";
import { UsFlag } from "@/components/icons/UsFlag";
import { ukMenuItems } from "@/data/uk-pages";

const EMAIL = "contact@pageonelawyers.com";
const PHONE_DISPLAY = "+91 98880 66671";
const PHONE_TEL = "+919888066671";
const WHATSAPP = "https://wa.me/919888066671?text=Hi%20PageOne%20Lawyers%2C%20I%27d%20like%20to%20learn%20more%20about%20your%20SEO%20services.";


function TopBar() {
  return (
    <div className="hidden md:block bg-[var(--navy-deep)] text-primary-foreground text-xs">
      <div className="mx-auto max-w-7xl px-6 h-9 flex items-center justify-between gap-4">
        <div className="flex items-center gap-5 min-w-0">
          <a href={`mailto:${EMAIL}`} className="inline-flex items-center gap-1.5 hover:text-[var(--gold)] transition">
            <Mail className="h-3.5 w-3.5" /> <span className="truncate">{EMAIL}</span>
          </a>
          <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-1.5 hover:text-[var(--gold)] transition">
            <Phone className="h-3.5 w-3.5" /> {PHONE_DISPLAY}
          </a>
        </div>
        <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-semibold text-[var(--gold)] hover:text-primary-foreground transition">
          <MessageCircle className="h-3.5 w-3.5" /> Chat on WhatsApp
        </a>
      </div>
    </div>
  );
}

function Dropdown({ label, children, wide = false, active = false, panelClass }: { label: string; children: React.ReactNode; wide?: boolean; active?: boolean; panelClass?: string }) {
  return (
    <div className="relative group">
      <button className={`inline-flex items-center gap-1 px-2 text-sm transition py-2 ${active ? "font-semibold text-foreground border-b-2 border-[var(--gold)]" : "font-medium text-foreground/80 hover:text-foreground"}`}>
        {label} <ChevronDown className="h-3.5 w-3.5 transition group-hover:rotate-180" />
      </button>
      <div className={`absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ${panelClass ?? (wide ? "w-[720px]" : "w-64")}`}>
        <div className="rounded-xl border border-border bg-card shadow-[var(--shadow-elegant)] p-4">
          {children}
        </div>
      </div>
    </div>
  );
}

type ResourcePath = "/best-law-firm-seo-companies" | "/case-studies" | "/blog" | "/guides" | "/glossary";

/** Resources dropdown row: decorative icon tile + title + description. */
function ResourceRow({ to, title, desc, active = false, ariaCurrent }: {
  to: ResourcePath;
  title: string;
  desc: string;
  active?: boolean;
  ariaCurrent?: "page";
}) {
  const Icon = resourceIcons[to];
  return (
    <Link to={to} aria-current={ariaCurrent} className={`group/item flex items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground/80 transition duration-150 hover:bg-muted hover:text-foreground ${active ? "bg-muted" : ""}`}>
      {Icon && (
        <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] border border-[#f0dfbd] bg-[#fbf3e3] text-[#b9791a] transition duration-150 group-hover/item:border-[#eba941] group-hover/item:bg-[#eba941] group-hover/item:text-[#0b1626]">
          <Icon size={20} strokeWidth={1.8} aria-hidden="true" />
        </span>
      )}
      <span className="min-w-0">
        <span className="block font-semibold text-foreground">{title}</span>
        <span className="block text-xs text-muted-foreground">{desc}</span>
      </span>
    </Link>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<string | null>(null);
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  const onGlossary = pathname === "/glossary" || pathname.startsWith("/glossary/");
  const linkCls = "text-sm font-medium text-foreground/80 hover:text-foreground transition";
  const itemCls = "block rounded-md px-3 py-2 text-sm text-foreground/80 hover:bg-muted hover:text-foreground transition";


  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
      <TopBar />
        <div className="mx-auto grid h-16 max-w-7xl grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-5 sm:px-6 lg:flex lg:justify-between">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-hero-gradient text-primary-foreground font-display font-bold">P1</span>
          <span className="font-display text-lg font-semibold tracking-tight truncate">PageOne Lawyers</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-0">
          <Link to="/about" className={`${linkCls} px-2 py-2`}>About</Link>

          <Dropdown label="Services" wide panelClass="w-[720px] xl:w-[1080px] xl:left-[-220px] xl:translate-x-0">
            <div className="flex flex-col gap-4 xl:flex-row">
            <div className="min-w-0 flex-1">
            <div className="mb-2 px-3 flex items-center justify-between">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold"><UsFlag />For US law firms</span>
              <Link to="/services" className="text-xs font-semibold text-primary hover:text-[var(--gold-deep)]">View all →</Link>
            </div>
            <div className="grid grid-cols-2 gap-1">
              {services.map((s) => (
                <Link key={s.slug} to="/services/$slug" params={{ slug: s.slug }} className={itemCls}>
                  <div className="font-semibold text-foreground">{s.name}</div>
                  <div className="text-xs text-muted-foreground line-clamp-1">{s.tagline}</div>
                </Link>
              ))}
            </div>
            </div>
            <div className="shrink-0 rounded-xl border border-[#ebe4d4] bg-[#f4f1ea] px-2 pt-3.5 pb-2.5 xl:w-[300px]">
              <div className="mb-2 flex items-center justify-between px-3">
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold"><UkFlag />For UK firms</span>
                <span className="rounded-full bg-[#fbeed3] px-2 py-0.5 text-[10.5px] font-bold text-[#8a5a0b]">UK</span>
              </div>
              <div className="grid gap-1 sm:grid-cols-3 xl:grid-cols-1">
                {ukMenuItems.map((u) => (
                  <a key={u.href} href={u.href} className="block rounded-md px-3 py-2 text-sm text-foreground/80 transition hover:bg-white hover:text-foreground hover:shadow-[var(--shadow-card)]">
                    <div className="font-semibold text-foreground">{u.title}</div>
                    <div className="text-xs text-muted-foreground line-clamp-1">{u.description}</div>
                  </a>
                ))}
              </div>
              <p className="mx-3 mt-2 border-t border-dashed border-[#ddd3bd] pt-2 text-[11.5px] leading-snug text-muted-foreground">Solicitors and law firms across England, Scotland, Wales and Northern Ireland.</p>
            </div>
            </div>
          </Dropdown>

          <Dropdown label="Practice Areas" wide>
            <div className="mb-2 px-3 flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">All Practices</span>
              <Link to="/practice-areas" className="text-xs font-semibold text-primary hover:text-[var(--gold-deep)]">View all →</Link>
            </div>
            <div className="grid grid-cols-2 gap-1">
              {practiceAreas.map((p) => {
                const Icon = practiceIcons[p.slug];
                return (
                  <Link key={p.slug} to="/practice-areas/$slug" params={{ slug: p.slug }} className="group/item flex items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground/80 hover:bg-muted hover:text-foreground transition duration-150">
                    {Icon && (
                      <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] border border-[#f0dfbd] bg-[#fbf3e3] text-[#b9791a] transition duration-150 group-hover/item:border-[#eba941] group-hover/item:bg-[#eba941] group-hover/item:text-[#0b1626]">
                        <Icon size={20} strokeWidth={1.8} aria-hidden="true" />
                      </span>
                    )}
                    <span className="min-w-0">
                      <span className="block font-semibold text-foreground">{p.shortName}</span>
                      <span className="block text-xs text-muted-foreground line-clamp-1">{p.name}</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </Dropdown>

          <LocationsMenu />

          <Dropdown label="Resources" active={onGlossary} panelClass="w-80">
            <ResourceRow to="/best-law-firm-seo-companies" title="Best SEO Companies" desc="How to choose the right agency" />
            <ResourceRow to="/case-studies" title="Case Studies" desc="Real results, real firms" />
            <ResourceRow to="/blog" title="Blog" desc="Legal SEO insights" />
            <ResourceRow to="/guides" title="PDF Guides" desc="Free downloadable playbooks" />
            <ResourceRow to="/glossary" title="Glossary" desc="SEO terms explained for law firms" active={onGlossary} ariaCurrent={pathname === "/glossary" ? "page" : undefined} />
          </Dropdown>

          <Link to="/pricing" className={`${linkCls} px-2 py-2`}>Pricing</Link>
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <Link to="/law-firm-seo-audit" className="text-sm font-semibold text-primary hover:text-[var(--gold-deep)] transition">Free SEO Audit</Link>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="relative inline-flex items-center rounded-md bg-gold-gradient text-[var(--navy-deep)] px-5 py-2.5 text-sm font-bold shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-elegant)] hover:-translate-y-0.5 transition ring-1 ring-[var(--gold-deep)]/40">
            Book a Call
          </a>
        </div>

        <button onClick={() => setOpen(!open)} className="min-h-11 min-w-11 shrink-0 p-2 lg:hidden" aria-label={open ? "Close menu" : "Menu"} aria-expanded={open}>
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-5 py-3 border-b border-border flex flex-col gap-2 bg-muted/50 sm:px-6">
            <a href={`mailto:${EMAIL}`} className="text-sm inline-flex items-center gap-2 text-foreground"><Mail className="h-4 w-4" /> {EMAIL}</a>
            <a href={`tel:${PHONE_TEL}`} className="text-sm inline-flex items-center gap-2 text-foreground"><Phone className="h-4 w-4" /> {PHONE_DISPLAY}</a>
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="text-sm inline-flex items-center gap-2 font-semibold text-[var(--gold-deep)]"><MessageCircle className="h-4 w-4" /> WhatsApp Chat</a>
          </div>
          <nav className="px-4 py-3 flex flex-col gap-1">
            <Link to="/about" className="px-3 py-2.5 text-foreground font-medium" onClick={() => setOpen(false)}>About</Link>

            {[
              { key: "services", label: "Services", items: services.map((s) => ({ to: "/services/$slug" as const, params: { slug: s.slug }, label: s.name })), viewAll: "/services" },
              { key: "practice", label: "Practice Areas", items: practiceAreas.map((p) => ({ to: "/practice-areas/$slug" as const, params: { slug: p.slug }, label: p.name })), viewAll: "/practice-areas" },
            ].map((sec) => (
              <div key={sec.key}>
                <button onClick={() => setMobileSection(mobileSection === sec.key ? null : sec.key)} className="w-full flex items-center justify-between px-3 py-2.5 text-foreground font-medium">
                  {sec.label} <ChevronDown className={`h-4 w-4 transition ${mobileSection === sec.key ? "rotate-180" : ""}`} />
                </button>
                {mobileSection === sec.key && (
                  <div className="pl-4 flex flex-col gap-0.5 pb-2">
                    {sec.key === "services" && (
                      <div className="mb-1 flex items-center gap-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><UsFlag />For US law firms</div>
                    )}
                    <Link to={sec.viewAll} className="px-3 py-2 text-sm font-semibold text-primary" onClick={() => setOpen(false)}>View all →</Link>
                    {sec.items.map((it) => (
                      <Link key={it.params.slug} to={it.to} params={it.params} className="px-3 py-2 text-sm text-foreground/80" onClick={() => setOpen(false)}>{it.label}</Link>
                    ))}
                    {sec.key === "services" && (
                      <div className="mt-2 rounded-xl bg-[#f4f1ea] px-3 py-3">
                        <div className="mb-1 flex items-center gap-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><UkFlag />For UK firms</div>
                        {ukMenuItems.map((u) => (
                          <a key={u.href} href={u.href} className="block px-3 py-2 text-sm text-foreground/80" onClick={() => setOpen(false)}>{u.title}</a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            <MobileLocationsMenu expanded={mobileSection === "loc"} onToggle={() => setMobileSection(mobileSection === "loc" ? null : "loc")} onNavigate={() => setOpen(false)} />

            <div>
              <button onClick={() => setMobileSection(mobileSection === "res" ? null : "res")} className="w-full flex items-center justify-between px-3 py-2.5 text-foreground font-medium">
                Resources <ChevronDown className={`h-4 w-4 transition ${mobileSection === "res" ? "rotate-180" : ""}`} />
              </button>
              {mobileSection === "res" && (
                <div className="pl-4 flex flex-col gap-0.5 pb-2">
                  <Link to="/best-law-firm-seo-companies" className="px-3 py-2 text-sm text-foreground/80" onClick={() => setOpen(false)}>Best SEO Companies</Link>
                  <Link to="/case-studies" className="px-3 py-2 text-sm text-foreground/80" onClick={() => setOpen(false)}>Case Studies</Link>
                  <Link to="/blog" className="px-3 py-2 text-sm text-foreground/80" onClick={() => setOpen(false)}>Blog</Link>
                  <Link to="/guides" className="px-3 py-2 text-sm text-foreground/80" onClick={() => setOpen(false)}>PDF Guides</Link>
                  <Link to="/glossary" aria-current={pathname === "/glossary" ? "page" : undefined} className={`px-3 py-2 text-sm ${onGlossary ? "font-semibold text-foreground" : "text-foreground/80"}`} onClick={() => setOpen(false)}>Glossary</Link>
                </div>
              )}
            </div>

            <Link to="/pricing" className="px-3 py-2.5 text-foreground font-medium" onClick={() => setOpen(false)}>Pricing</Link>

            <div className="mt-3 flex flex-col gap-2 px-3">
              <Link to="/law-firm-seo-audit" className="text-center rounded-md border border-primary text-primary px-4 py-2.5 text-sm font-semibold" onClick={() => setOpen(false)}>Free SEO Audit</Link>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="text-center rounded-md bg-gold-gradient text-[var(--navy-deep)] px-4 py-3 text-sm font-bold shadow-[var(--shadow-card)]" onClick={() => setOpen(false)}>Book a Call</a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}